import { type OperationVariables, useQuery } from '@apollo/client'
import { useIsUserSignedIn } from '@/src/common/hooks/useIsUserSignedIn'
import { useEffect } from 'react'

/** only use {@link useOptionallyAuthdQuery} if you are sure you the resolver doesn't require auth */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export const useOptionallyAuthdQuery = useQuery

/** the provided query will be `skip`ped if the user is not fully logged in (i.e. has authenticated with their access token _and_ finished creating their account with a phone number) */
export function useLoggedInQuery<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
>(...[query, options]: Parameters<typeof useQuery<TData, TVariables>>) {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { isSignedInWithPhoneNumber, isSignedIn } = useIsUserSignedIn()

  const skip = !isSignedInWithPhoneNumber || options?.skip

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const result = useQuery(query, { ...options, skip })

  const { cache } = result.client

  useEffect(
    /**
     * just using {@link skip} may not be enough because if the query uses a cache,
     * that cache would not be cleared when the token updates (e.g. logging out and
     * logging into a new account) in the same session. therefore, we should clear
     * the cache when the user signs out.
     */
    function clearCacheWhenSigningOut() {
      !isSignedIn && cache.updateQuery({ query }, () => {})
    },
    [isSignedIn, cache, query],
  )

  return result
}
